import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import { categories, allArticles, faqs } from '@/mocks/data';

const featuredArticles = allArticles.filter((a) =>
  ['ntr-writing-beginner-guide', 'ntr-psychology-analysis', 'ntr-manga-top10'].includes(a.slug)
);

const recentArticles = allArticles.slice(0, 6);

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'NTR大师',
  url: 'https://ntrmaster.com',
  description: '专业NTR内容创作与鉴赏指南平台',
  sameAs: [],
};

const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'NTR大师',
  url: 'https://ntrmaster.com',
  description: '专业NTR内容创作与鉴赏指南平台，提供NTR题材深度解析、创作技巧与作品推荐',
  potentialAction: {
    '@type': 'SearchAction',
    target: 'https://ntrmaster.com/search?q={search_term_string}',
    'query-input': 'required name=search_term_string',
  },
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      name: '首页',
      item: 'https://ntrmaster.com',
    },
  ],
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
};

export default function Home() {
  return (
    <>
      <SEOHead
        title="ntr大师 | 专业NTR内容创作与鉴赏指南"
        description="ntr大师为您提供最全面的NTR题材深度解析、创作技巧与作品推荐。无论您是NTR爱好者还是内容创作者，ntr大师助您深入理解这一独特文化现象，掌握核心创作要素与鉴赏方法。"
        keywords="ntr大师,NTR创作技巧,NTR作品推荐,NTR题材解析,NTR文化鉴赏,NTR剧情要素,NTR内容指南"
        canonicalUrl="https://ntrmaster.com"
        ogImage="https://readdy.ai/api/search-image?query=Dark%20atmospheric%20anime%20style%20illustration%20with%20warm%20golden%20accents%20representing%20storytelling%20and%20creative%20writing%2C%20cel%20shaded%20art%2C%20moody%20cinematic%20lighting&width=1200&height=630&seq=ntr-home-og&orientation=landscape"
        schemaData={[organizationSchema, websiteSchema, breadcrumbSchema, faqSchema]}
      />
      <ScrollToTop />
      <div className="min-h-screen bg-background-950">
        <Navbar />

        <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Dark%20atmospheric%20anime%20style%20scene%20with%20antique%20wooden%20desk%20covered%20in%20scattered%20manuscripts%20and%20a%20fountain%20pen%2C%20warm%20golden%20lamp%20light%20casting%20dramatic%20shadows%2C%20cel%20shaded%20illustration%2C%20rich%20amber%20and%20dark%20brown%20tones%2C%20moody%20cinematic%20lighting%2C%20creative%20writing%20atmosphere%2C%20dust%20particles%20in%20light%20beam&width=1800&height=900&seq=ntr-hero-bg&orientation=landscape"
            alt="NTR大师 - 专业NTR内容创作与鉴赏指南"
            title="NTR大师 - 专业NTR内容创作与鉴赏指南"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 w-full">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-4 font-heading tracking-wide">
              NTR大师
            </h1>
            <p className="text-base md:text-lg text-foreground-100 max-w-2xl leading-relaxed mb-6">
              专业NTR内容创作与鉴赏指南平台，助您深入理解NTR文化的独特魅力
            </p>
            <div className="flex flex-col sm:flex-row items-center gap-3">
              <Link
                to="/category/ntr-writing"
                className="px-6 py-3 bg-primary-500 text-background-950 font-medium rounded-lg hover:bg-primary-400 transition-colors whitespace-nowrap cursor-pointer text-sm"
              >
                开始探索
              </Link>
              <Link
                to="/about"
                className="px-6 py-3 border border-white/30 text-white rounded-lg hover:bg-white/10 transition-colors whitespace-nowrap cursor-pointer text-sm"
              >
                了解更多
              </Link>
            </div>
          </div>
        </section>

        <section className="w-full max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-20">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-100 font-heading">
              内容分类
            </h2>
            <p className="mt-3 text-foreground-400 text-sm max-w-xl mx-auto">
              五大核心板块，覆盖NTR文化创作与鉴赏的方方面面
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="group p-6 rounded-lg bg-background-900 border border-background-800 hover:border-primary-400/30 transition-all cursor-pointer"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-500/10 text-primary-400 mb-4 group-hover:bg-primary-500/20 transition-colors">
                  <i className={`${cat.icon} text-xl`}></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-200 mb-2 font-heading">
                  {cat.name}
                </h3>
                <p className="text-xs text-foreground-500 leading-relaxed mb-3">
                  {cat.description}
                </p>
                <span className="text-xs text-primary-400">
                  {cat.articleCount} 篇文章
                </span>
              </Link>
            ))}
          </div>
        </section>

        <section className="w-full bg-background-900 py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-100 font-heading">
                  精选文章
                </h2>
                <p className="mt-2 text-foreground-400 text-sm">编辑精选的高质量NTR内容</p>
              </div>
              <Link to="/category/ntr-writing" className="text-sm text-primary-400 hover:text-primary-300 transition-colors whitespace-nowrap cursor-pointer">
                查看全部 <i className="ri-arrow-right-line ml-1"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {featuredArticles.map((article) => {
                const cat = categories.find((c) => c.slug === article.category);
                return (
                  <Link
                    key={article.slug}
                    to={`/article/${article.slug}`}
                    className="group bg-background-950 rounded-lg overflow-hidden border border-background-800 hover:border-primary-400/20 transition-all cursor-pointer"
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        title={`${article.title} - NTR大师`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-3 left-3">
                        <span className="px-2.5 py-1 bg-primary-500/80 text-background-950 text-xs rounded-full font-medium">
                          {cat?.name || ''}
                        </span>
                      </div>
                    </div>
                    <div className="p-5">
                      <h3 className="text-base font-semibold text-foreground-200 group-hover:text-primary-300 transition-colors mb-2 font-heading line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2 mb-3">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center gap-3 text-xs text-foreground-600">
                        <span>{article.date}</span>
                        <span>·</span>
                        <span>{article.readTime}</span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        <section className="w-full max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-20">
          <div className="flex items-center justify-between mb-10">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-100 font-heading">
                最新文章
              </h2>
              <p className="mt-2 text-foreground-400 text-sm">持续更新的NTR内容资源</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {recentArticles.map((article) => {
              const cat = categories.find((c) => c.slug === article.category);
              return (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="group flex gap-4 p-4 rounded-lg bg-background-900 border border-background-800 hover:border-primary-400/20 transition-all cursor-pointer"
                >
                  <div className="w-20 h-20 flex-shrink-0 rounded-md overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      title={`${article.title} - NTR大师`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="flex flex-col justify-between min-w-0">
                    <div>
                      <span className="text-[10px] text-primary-400 font-medium">{cat?.name}</span>
                      <h3 className="text-sm font-semibold text-foreground-300 group-hover:text-primary-300 transition-colors mt-1 line-clamp-2 font-heading">
                        {article.title}
                      </h3>
                    </div>
                    <span className="text-[11px] text-foreground-600">{article.date}</span>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>

        <section className="w-full bg-background-900 py-16 md:py-20">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-100 font-heading">
                常见问题
              </h2>
              <p className="mt-2 text-foreground-400 text-sm">关于NTR及NTR大师的常见疑问解答</p>
            </div>
            <div className="space-y-3">
              {faqs.map((faq, index) => (
                <details key={index} className="group bg-background-950 rounded-lg border border-background-800">
                  <summary className="px-5 py-4 cursor-pointer flex items-center justify-between text-sm font-medium text-foreground-300 hover:text-foreground-200 transition-colors">
                    {faq.question}
                    <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <div className="px-5 pb-4 text-sm text-foreground-500 leading-relaxed">
                    {faq.answer}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full py-16 md:py-20 relative overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Dark%20atmospheric%20anime%20style%20scene%20with%20rays%20of%20golden%20light%20breaking%20through%20darkness%2C%20inspiring%20and%20hopeful%20mood%2C%20cel%20shaded%20illustration%2C%20warm%20amber%20light%20against%20deep%20shadows%2C%20dramatic%20lighting&width=1800&height=600&seq=ntr-cta-bg&orientation=landscape"
            alt="NTR大师内容指南"
            title="NTR大师内容指南"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-black/60"></div>
          <div className="relative max-w-3xl mx-auto text-center px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-bold text-white font-heading mb-4">
              准备好深入探索NTR的世界了吗？
            </h2>
            <p className="text-foreground-200 text-sm mb-6 leading-relaxed">
              无论您是创作者还是鉴赏者，NTR大师都将成为您最可靠的伙伴
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/category/ntr-writing"
                className="px-6 py-3 bg-primary-500 text-background-950 font-medium rounded-lg hover:bg-primary-400 transition-colors whitespace-nowrap cursor-pointer text-sm"
              >
                立即探索
              </Link>
              <Link
                to="/about"
                className="px-6 py-3 border border-white/30 text-white rounded-lg hover:bg-white/10 transition-colors whitespace-nowrap cursor-pointer text-sm"
              >
                关于我们
              </Link>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}